import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {dropColorNoRendererReads} from "#material/nodes/texture/texture-from-set/validation"
import {versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {vec3} from "#material/types/vector"
import type {ImageTextureExtension, ImageTextureInterpolation, ImageTextureProjection} from "#material/nodes/texture/tex-image/types"
import {imageTextureExtensionSchema, imageTextureInterpolation, imageTextureProjection} from "#material/nodes/texture/tex-image/types"
import {LegacyTextureType} from "#material/types/texture"

export interface TextureFromSetSockets extends MaterialNodeSockets {
    inputs: {
        uv: "vector" // found in inputs: point,color; found in settings: input,vector; inferred from data: null,float3
    }
    settings: {
        textureSetRevisionId: "string" // found in inputs: -; found in settings: string; inferred from data: string,undefined
        uv: "vector" // found in inputs: point,color; found in settings: input,vector; inferred from data: null,float3
    }
    settingsExtensions: {
        extension?: ImageTextureExtension
        interpolation?: ImageTextureInterpolation
        mapAssignmentTextureType?: LegacyTextureType // found in inputs: -; found in settings: string; inferred from data: string
        projection?: ImageTextureProjection
    }
    outputs: {
        color: "color"
        alpha: "float"
    }
}

export const textureFromSetDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.CmTextureFromSet,
    },
    nodeClass: "TextureFromSet",
    versionChain: versionChain([dropColorNoRendererReads]),
    hierarchical: true,
    ui: {
        label: "Texture From Set",
        color: "#9e6034",
        width: 175,
        items: [
            {name: "color", valueType: "color", label: "Texture", output: true},
            {name: "alpha", valueType: "float", label: "Alpha", output: true},
            {custom: true, componentClass: "textureFromSet"},
            {name: "uv", valueType: "vector", label: "UV", input: true, setting: true},
        ],
        support: {
            cycles: true,
            three: {full: false, remark: "Some filters/extension modes unsupported"},
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, alpha: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({uv: optionalMaterialInput}),
            settings: z.strictObject({
                extension: z.optional(imageTextureExtensionSchema),
                interpolation: z.optional(imageTextureInterpolation),
                mapAssignmentTextureType: z.optional(z.enum(LegacyTextureType)),
                projection: z.optional(imageTextureProjection),
                textureSetRevisionId: z.optional(z.string()),
                uv: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TextureFromSetSockets>
